import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaFacebook,
  FaInstagram,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaTruck, // Fixed icon
  FaDollarSign,
  FaHeart,
  FaRupeeSign,
  FaPhoneAlt,
} from "react-icons/fa";
import { motion } from "framer-motion";

// 🎨 DYNAMIC COLOR SCHEME VARIABLES 🎨
const ACCENT_COLOR_HEX = "#A0522D"; // Sienna / Dark Caramel
const BG_COLOR_HEX = "#F5F5F5"; // WhiteSmoke / Light Beige

// --- SLIDE DATA (Corrected Paths to /public/hero-section) ---
const slides = [
  {
    img: "/images/hero_section/family_slide_3.png",
    headline: "Ethnic Elegance for the Whole Family",
    subtext: "Matching sets and traditional wear for every festive occasion.",
    alt: "Family wearing matching traditional Indian outfits",
  },
  {
    img: "/images/hero_section/saree_hero_slide2.png",
    headline: "Luxury Drapes & Intricate Embroidery",
    subtext: "Discover the latest collection of handloom and silk sarees.",
    alt: "Close-up of green saree with gold embroidery",
  },
  {
    img: "/images/hero_section/kids_hero_slide1.png",
    headline: "Vibrant Styles for Your Little Ones",
    subtext: "Comfortable and colorful kidswear for play and celebration.",
    alt: "Two children happily running in ethnic Indian clothes",
  },
  {
    img: "/images/hero_section/family_slide_5.png",
    headline: "Celebrate Traditions, Together",
    subtext: "Outfit solutions for large gatherings and weddings. Style for every generation.",
    alt: "Extended family portrait in traditional Indian attire",
  },
  {
    img: "/images/hero_section/sigle_girl_slide_4.png",
    headline: "Joyful Lehengas & Designer Kids Wear",
    subtext: "Sparkle in our vibrant party and festive collection for kids.",
    alt: "A young girl twirling in a pink and orange lehenga",
  },
];

// Framer Motion Animation Variants
const containerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.2,
    },
  },
};

const itemVariant = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100 } },
};
// ------------------------------------

function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideInterval = 8000;

  // Effect to handle the automatic slide transition
  useEffect(() => {
    const timer = setInterval(() => {
      // Loop back to the first slide when the end is reached
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
    }, slideInterval);

    // Cleanup function to clear the interval when the component unmounts
    return () => clearInterval(timer);
  }, []);

  const currentData = slides[currentSlide];

  return (
    <div className="bg-white">
      {/* 🚀 HERO SECTION - (EXISTING CODE) 🚀 */}
      <section className={`relative min-h-[80vh] flex items-center bg-[${BG_COLOR_HEX}]`}>
        <div className="max-w-7xl mx-auto flex w-full h-full">

          {/* LEFT TEXT (50% Width) */}
          <div className={`w-full md:w-1/2 flex flex-col justify-center text-center md:text-left z-10 p-8 md:p-6 lg:p-12 bg-[${BG_COLOR_HEX}] md:bg-transparent`}>

            {/* Dynamic Headline */}
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900">
              {currentData.headline}
            </h1>

            {/* Dynamic Subtext - Uses ACCENT_COLOR_HEX */}
            <h2 className="text-xl md:text-3xl mt-2 font-semibold" style={{ color: ACCENT_COLOR_HEX }}>
              {currentData.subtext}
            </h2>

            <p className="text-gray-700 mt-4 text-lg">
              Discover the latest fashion for women, men and kids. From sarees
              and kurtis to Western outfits and kidswear – all under one roof in{" "}
              <span className="font-semibold">Kaij, Maharashtra.</span>
            </p>

            {/* CTA BUTTONS - Uses ACCENT_COLOR_HEX */}
            <div className="flex gap-4 mt-6 justify-center md:justify-start">
              <button
                className={`text-white px-6 py-2 rounded-md text-lg transition duration-300 hover:opacity-90`}
                style={{ backgroundColor: ACCENT_COLOR_HEX, '--tw-ring-color': ACCENT_COLOR_HEX }}
              >
                View Collection
              </button>
              {/* WhatsApp button kept green for universal recognition */}
              <a
                href="https://wa.me/91XXXXXXXXXX"
                target="_blank"
                rel="noreferrer"
                className="bg-green-600 text-white px-6 py-2 rounded-md text-lg hover:bg-green-700 transition duration-300"
              >
                WhatsApp Us
              </a>
            </div>

            {/* SOCIAL ICONS */}
            <div className="flex gap-5 text-3xl mt-6 justify-center md:justify-start">
              <a href="#" className="text-blue-600 hover:text-blue-800" aria-label="Facebook">
                <FaFacebook />
              </a>
              <a href="#" className="text-pink-600 hover:text-pink-800" aria-label="Instagram">
                <FaInstagram />
              </a>
              <a
                href="https://wa.me/91XXXXXXXXXX"
                className="text-green-600 hover:text-green-800"
                aria-label="WhatsApp"
              >
                <FaWhatsapp />
              </a>
            </div>
          </div>

          {/* RIGHT IMAGE (50% Width, Full Height Background Slider) */}
          <div className="hidden md:block w-1/2 absolute right-0 top-0 h-full">
            {slides.map((slide, index) => (
              <img
                key={index}
                src={slide.img}
                alt={slide.alt}
                // Image fills the container
                className={`
                  absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out
                  ${index === currentSlide ? 'opacity-100' : 'opacity-0'}
                `}
              />
            ))}

            {/* Navigation Dots for manual control - Active dot uses ACCENT_COLOR_HEX */}
            <div className="absolute bottom-10 left-0 right-0 flex justify-center space-x-2 z-20">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors duration-300 ${index === currentSlide ? '' : 'bg-white bg-opacity-75 hover:bg-opacity-100'}`}
                  style={index === currentSlide ? { backgroundColor: ACCENT_COLOR_HEX } : null}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <hr className="max-w-6xl mx-auto border-gray-200" />

      {/* CATEGORY SECTION - (EXISTING CODE) */}
      <motion.section
        className="py-14 bg-white mb-20"
        variants={containerVariant} // Apply container variant
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.1 }} // Runs once when 10% of the section is visible
      >
        <h2 className="text-3xl text-center font-bold text-gray-800 mb-10" style={{ color: ACCENT_COLOR_HEX }}>
          Shop by Category
        </h2>

        <motion.div
          className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 px-6"
          variants={containerVariant} // Apply container variant for the category cards
        >
          {/* Each CategoryCard now uses motion.div wrapper with itemVariant */}
          <motion.div variants={itemVariant}>
            <Link to="/women">
              <CategoryCard
                title="Women’s Wear"
                img="/images/categories/women.jpg"
              />
            </Link>
          </motion.div>

          <motion.div variants={itemVariant}>
            <Link to="/men">
              <CategoryCard title="Men’s Wear" img="/images/categories/men.jpg" />
            </Link>
          </motion.div>

          <motion.div variants={itemVariant}>
            <Link to="/kids">
              <CategoryCard
                title="Kids Clothing"
                img="/images/categories/kids.jpg"
              />
            </Link>
          </motion.div>

          <motion.div variants={itemVariant}>
            <Link to="/collections">
              <CategoryCard
                title="Sarees & Ethnic"
                img="/images/categories/saree.jpg"
              />
            </Link>
          </motion.div>
        </motion.div>
      </motion.section>

      <hr className="max-w-6xl mx-auto border-gray-200" />

      {/* 🌟 NEW: WHY CHOOSE US SECTION (Framer Motion: once: false) 🌟 */}
      <motion.section
        className={`py-16 mb-20 bg-[${BG_COLOR_HEX}]`}
        variants={containerVariant}
        initial="hidden"
        whileInView="visible"
        // 👈 CHANGE: Allows animation to re-trigger on scroll
        viewport={{ once: false, amount: 0.1 }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl text-center font-bold text-gray-900 mb-12" style={{ color: ACCENT_COLOR_HEX }}>
            Why Choose Shakshi Collection?
          </h2>

          <div className="flex flex-col md:flex-row gap-12 items-center">

            {/* LEFT COLUMN: Image Placeholder (50% Width) */}
            <motion.div
              className="w-full md:w-1/2 rounded-xl overflow-hidden shadow-2xl"
              variants={itemVariant}
            >
              <img
                src="/images/why_us_test_2.png"
                alt="Shakshi Collection Quality Garments"
                className="w-full h-80 object-cover"
                loading="lazy"
              />
            </motion.div>

            {/* RIGHT COLUMN: Value Propositions (50% Width) */}
            <motion.div
              className="w-full md:w-1/2 space-y-8"
              variants={containerVariant}
            >
              <motion.div className="flex items-start gap-4" variants={itemVariant}>
                <FaHeart size={30} style={{ color: ACCENT_COLOR_HEX }} className="mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Premium Quality Fabrics</h3>
                  <p className="text-gray-700 mt-1">We hand-select traditional silks, durable cottons, and modern blends to ensure comfort and longevity in every garment.</p>
                </div>
              </motion.div>

              <motion.div className="flex items-start gap-4" variants={itemVariant}>
                <FaRupeeSign size={30} style={{ color: ACCENT_COLOR_HEX }} className="mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Affordable Family Style</h3>
                  <p className="text-gray-700 mt-1">Exceptional value and competitive pricing, making it easy for the whole family to dress in the latest trends without compromise.</p>
                </div>
              </motion.div>

              <motion.div className="flex items-start gap-4" variants={itemVariant}>
                <FaMapMarkerAlt size={30} style={{ color: ACCENT_COLOR_HEX }} className="mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Local and Reliable Service</h3>
                  <p className="text-gray-700 mt-1">Based in Kaij, Maharashtra, we offer styling advice and tailor-made service focused on local community needs.</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>
      <hr className="max-w-6xl mx-auto border-gray-200" />

      {/* 🎬 NEW: FEATURED VIDEO COLLECTION SECTION 🎬 */}
      <motion.section
        className="py-16 bg-white mb-20" // ADDED mb-20
        variants={containerVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.1 }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl text-center font-bold text-gray-900 mb-12" style={{ color: ACCENT_COLOR_HEX }}>
            The Grand Collection: Weddings & Celebrations
          </h2>

          <div className="flex flex-col md:flex-row gap-12 items-center">

            {/* LEFT COLUMN: Text Description */}
            <motion.div
              className="w-full md:w-1/2 space-y-6 md:order-1 order-2"
              variants={containerVariant}
            >
              <motion.p className="text-gray-700 text-lg" variants={itemVariant}>
                Experience the luxury of our curated festive collection. Whether it’s a wedding, a major festival, or a grand family event, we have the perfect attire for every member.
              </motion.p>
              <motion.p className="text-gray-700 text-lg" variants={itemVariant}>
                Find exclusive sarees, elegant men’s bandhgalas, and adorable kids’ outfits designed for comfort and style. Our collection brings together traditional artistry with modern trends.
              </motion.p>
              <motion.p className="text-gray-700 text-lg font-semibold" variants={itemVariant} style={{ color: ACCENT_COLOR_HEX }}>
                Visit our store for a personalized styling consultation and see the quality firsthand!
              </motion.p>
              <motion.a
                href="#"
                className={`inline-block text-white px-8 py-3 rounded-lg font-semibold text-lg transition duration-300 hover:opacity-90 mt-4 shadow-lg`}
                style={{ backgroundColor: ACCENT_COLOR_HEX }}
                variants={itemVariant}
              >
                Explore Festive Wear
              </motion.a>
            </motion.div>

            {/* RIGHT COLUMN: Video Embed */}
            <motion.div
              className="w-full md:w-1/2 rounded-xl overflow-hidden shadow-2xl md:order-2 order-1"
              variants={itemVariant}
            >
              {/* VIDEO EMBED: Added controls, loop, muted, and autoplay */}
              <video
                src="/videos/short_video.mp4"
                autoPlay
                loop
                muted
                playsInline
                controls={false} // Hide controls for a cleaner background look
                className="w-full h-auto max-h-[500px] object-cover"
                loading="lazy"
                aria-label="Showcase video of festive and wedding clothing collection"
              />
            </motion.div>
          </div>
        </div>
      </motion.section>

      <hr className="max-w-6xl mx-auto border-gray-200" />

      {/* 📞 NEW: CONTACT US SECTION (Framer Motion: once: false) 📞 */}
    <motion.section
            className="py-16 bg-white"
            variants={containerVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.1 }}
        >
            <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ color: ACCENT_COLOR_HEX }}>
                        Visit Our Store & Connect
                    </h2>
                    <p className="text-gray-700 text-lg max-w-3xl mx-auto">
                        We are available during business hours for visits, custom orders, or quick chats.
                    </p>
                </div>
                
                {/* 🛑 MAIN LAYOUT GRID: MAP LEFT (2/3) | CARDS RIGHT (1/3) 🛑 */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    
                    {/* === COLUMN 1: MAP AND DIRECTIONS (TAKES 2/3 WIDTH ON LG) === */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="relative w-full">
                            <div className="relative rounded-xl overflow-hidden shadow-2xl border-2" style={{ borderColor: ACCENT_COLOR_HEX }}>
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6288.240885337272!2d76.06800852596675!3d18.715578088433812!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc53fbe29cebb4d%3A0xbd11e807fbcd5ebd!2sSakshi%20collection!5e1!3m2!1sen!2sin!4v1765017237605!5m2!1sen!2sin"
                                    width="100%"
                                    height="450"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    className="w-full"
                                    title="Shakshi Collection Street View"
                                    referrerPolicy="no-referrer-when-downgrade"
                                >
                                </iframe>
                            </div>
                        </div>

                        {/* Directions Link */}
                        <a
                            href="https://maps.google.com/?cid=13623925468759416509"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex justify-center items-center gap-3 text-lg text-blue-700 font-semibold hover:text-blue-900"
                        >
                            <FaMapMarkerAlt className="text-red-600 text-2xl" />
                            Get Directions to Kanadi Mali, Kaij
                        </a>
                    </div>
                    
                    {/* === COLUMN 2: CONTACT CARDS (TAKES 1/3 WIDTH ON LG) === */}
                    <motion.div
                        className="lg:col-span-1 space-y-6"
                        variants={containerVariant}
                    >
                        <h3 className="text-xl font-bold text-gray-800 text-left">Instant Connect</h3>

                        {/* Contact Card 1: WhatsApp */}
                        <motion.div
                            className="p-6 border-2 rounded-xl shadow-lg transition-shadow duration-300 hover:shadow-2xl w-full text-left"
                            variants={itemVariant}
                            style={{ borderColor: ACCENT_COLOR_HEX, backgroundColor: BG_COLOR_HEX }} 
                        >
                            <FaWhatsapp size={30} className="mb-2 text-green-600" />
                            <h4 className="text-lg font-bold mb-1 text-gray-800">WhatsApp Direct</h4>
                            <p className="text-gray-600 text-sm mb-3">Chat for stock, sizing, and remote shopping.</p>
                            <a
                                href="https://wa.me/91XXXXXXXXXX"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-block bg-green-600 text-white px-4 py-2 text-sm rounded-md font-semibold hover:bg-green-700 transition"
                            >
                                Start Chatting
                            </a>
                        </motion.div>

                        {/* Contact Card 2: Facebook */}
                        <motion.div
                            className="p-6 border-2 rounded-xl shadow-lg transition-shadow duration-300 hover:shadow-2xl w-full text-left"
                            variants={itemVariant}
                            style={{ borderColor: ACCENT_COLOR_HEX, backgroundColor: BG_COLOR_HEX }}
                        >
                            <FaFacebook size={30} style={{ color: ACCENT_COLOR_HEX }} className="mb-2" />
                            <h4 className="text-lg font-bold mb-1 text-gray-800">Facebook Updates</h4>
                            <p className="text-gray-600 text-sm mb-3">Follow us for daily new arrivals and offers.</p>
                            <a
                                href="#"
                                target="_blank"
                                rel="noreferrer"
                                className={`inline-block text-white px-4 py-2 text-sm rounded-md font-semibold transition duration-300 hover:opacity-90`}
                                style={{ backgroundColor: ACCENT_COLOR_HEX }}
                            >
                                Go to Facebook
                            </a>
                        </motion.div>
                        
                    </motion.div>
                    
                </div>
            </div>
        </motion.section>

      <hr className="max-w-6xl mx-auto border-gray-200" />

      {/* 📍 STORE INFO / LOCATION - EMBEDDED STREET VIEW 📍 */}
    
    </div>
  );
}

function CategoryCard({ title, img }) {
  return (
    <div className="group cursor-pointer">
      <div className="overflow-hidden rounded-lg shadow-md">
        <img
          src={img}
          alt={title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <h3 className="text-xl font-semibold text-center mt-3">{title}</h3>
    </div>
  );
}

export default HomePage;